import { Flex, Grid } from '@backstage/ui';
import { EntityCardBlueprint, EntityContentBlueprint } from '@backstage/plugin-catalog-react/alpha';
import {
  isMarketplaceAiResourceEntity,
  isPluginAiResourceEntity,
} from '@backstage/catalog-model/alpha';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
  isSystemEntity,
  type Entity,
} from '@backstage/catalog-model';
import { isKind } from '@backstage/plugin-catalog';
import { isApiEntityMcpServer } from '../../components/catalog/mcp/isApiEntityMcpServer';
import { isFullComponent } from './entityPredicates';

const isFullComponentOrSystem = (entity: Entity) =>
  isFullComponent(entity) || isSystemEntity(entity);

const markdownCard = EntityCardBlueprint.make({
  name: 'markdown',
  params: {
    loader: () =>
      import('../../components/catalog/EntityMarkdown/EntityMarkdownCard').then(m => (
        <m.EntityMarkdownCard />
      )),
  },
});

const codespacesCard = EntityCardBlueprint.make({
  name: 'codespaces',
  params: {
    type: 'info',
    filter: isFullComponent,
    loader: () =>
      import('@internal/backstage-plugin-github-codespaces').then(m => <m.EntityCodespacesCard />),
  },
});

const radixCard = EntityCardBlueprint.make({
  name: 'radix',
  params: {
    filter: isFullComponentOrSystem,
    loader: () => import('@internal/backstage-plugin-radix').then(m => <m.RadixCard />),
  },
});

const azureCard = EntityCardBlueprint.make({
  name: 'azure',
  params: {
    filter: isFullComponentOrSystem,
    loader: () => import('@internal/backstage-plugin-azure').then(m => <m.AzureCard />),
  },
});

const mcpServerInstallCard = EntityCardBlueprint.make({
  name: 'mcp-server-install',
  params: {
    type: 'info',
    filter: isApiEntityMcpServer,
    loader: () =>
      import('../../components/catalog/mcp/McpServerInstallCard').then(m => (
        <m.McpServerInstallCard />
      )),
  },
});

const templateDescriptionCard = EntityCardBlueprint.make({
  name: 'template-description',
  params: {
    filter: isKind('template'),
    loader: () =>
      import('../../components/catalog/TemplateDescriptionCard').then(m => (
        <m.TemplateDescriptionCard />
      )),
  },
});

const templatePrerequisitesCard = EntityCardBlueprint.make({
  name: 'template-prerequisites',
  params: {
    type: 'info',
    filter: isKind('template'),
    loader: () =>
      import('../../components/catalog/TemplatePrerequisites').then(m => (
        <m.TemplatePrerequisites />
      )),
  },
});

const marketplaceInstallCard = EntityCardBlueprint.make({
  name: 'marketplace-install',
  params: {
    type: 'info',
    filter: isMarketplaceAiResourceEntity,
    loader: () =>
      import('../../components/catalog/AiResourceInstallCards').then(m => (
        <m.MarketplaceInstallCard />
      )),
  },
});

const pluginInstallCard = EntityCardBlueprint.make({
  name: 'plugin-install',
  params: {
    type: 'info',
    filter: isPluginAiResourceEntity,
    loader: () =>
      import('../../components/catalog/AiResourceInstallCards').then(m => <m.PluginInstallCard />),
  },
});

const aiResourceHasPluginsCard = EntityCardBlueprint.make({
  name: 'ai-resource-has-plugins',
  params: {
    filter: isMarketplaceAiResourceEntity,
    loader: () =>
      import('../../components/catalog/AiResourceRelationCards/AiResourceRelationCards').then(m => (
        <m.AiResourceHasPluginsCard />
      )),
  },
});

const aiResourceHasSkillsCard = EntityCardBlueprint.make({
  name: 'ai-resource-has-skills',
  params: {
    filter: isPluginAiResourceEntity,
    loader: () =>
      import('../../components/catalog/AiResourceRelationCards/AiResourceRelationCards').then(m => (
        <m.AiResourceHasSkillsCard />
      )),
  },
});

const dependenciesContent = EntityContentBlueprint.make({
  name: 'dependencies',
  params: {
    path: '/dependencies',
    title: 'Dependencies',
    filter: isFullComponent,
    loader: () =>
      import('@backstage/plugin-catalog').then(m => (
        <Grid.Root columns={{ initial: '1', md: '2' }} gap="6">
          <m.EntityDependsOnComponentsCard />
          <m.EntityDependsOnResourcesCard />
        </Grid.Root>
      )),
  },
});

const mcpServerToolsContent = EntityContentBlueprint.make({
  name: 'mcp-server-tools',
  params: {
    path: '/tools',
    title: 'Tools',
    filter: isApiEntityMcpServer,
    loader: () =>
      import('../../components/catalog/mcp/McpServerToolsCard').then(m => <m.McpServerToolsCard />),
  },
});

const systemArchitectureContent = EntityContentBlueprint.make({
  name: 'system-architecture',
  params: {
    path: '/architecture',
    title: 'Architecture',
    filter: isKind('system'),
    loader: async () => {
      const [catalog, apiDocs] = await Promise.all([
        import('@backstage/plugin-catalog'),
        import('@backstage/plugin-api-docs'),
      ]);
      return (
        <Flex direction="column" gap="6">
          <catalog.EntityHasComponentsCard />
          <apiDocs.EntityHasApisCard />
          <catalog.EntityHasResourcesCard />
        </Flex>
      );
    },
  },
});

const systemDiagramContent = EntityContentBlueprint.make({
  name: 'system-diagram',
  params: {
    path: '/diagram',
    title: 'Diagram',
    filter: isKind('system'),
    loader: () =>
      import('@backstage/plugin-catalog-graph').then(m => (
        <m.EntityCatalogGraphCard
          direction={m.Direction.TOP_BOTTOM}
          title="System Diagram"
          height={700}
          relations={[
            RELATION_PART_OF,
            RELATION_HAS_PART,
            RELATION_API_CONSUMED_BY,
            RELATION_API_PROVIDED_BY,
            RELATION_CONSUMES_API,
            RELATION_PROVIDES_API,
            RELATION_DEPENDENCY_OF,
            RELATION_DEPENDS_ON,
          ]}
          unidirectional={false}
        />
      )),
  },
});

const templateDetailsContent = EntityContentBlueprint.make({
  name: 'template-details',
  params: {
    path: '/details',
    title: 'Details',
    filter: isKind('template'),
    loader: () => import('@backstage/plugin-catalog').then(m => <m.EntityAboutCard />),
  },
});

/** Varia's own entity cards and tabs; native plugins provide the rest. */
export const variaEntityCards = [
  markdownCard,
  codespacesCard,
  radixCard,
  azureCard,
  mcpServerInstallCard,
  templateDescriptionCard,
  templatePrerequisitesCard,
  marketplaceInstallCard,
  pluginInstallCard,
  aiResourceHasPluginsCard,
  aiResourceHasSkillsCard,
];

/** Ordered as the tabs should appear. */
export const variaEntityContents = [
  dependenciesContent,
  mcpServerToolsContent,
  systemArchitectureContent,
  systemDiagramContent,
  templateDetailsContent,
];
